/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState } from 'react';
import { AlertBar, Modal } from 'blb-components/GrommetComponents';
import { useToasts } from 'blb-components/AuthenticatedApp/Toast';
import { Text } from 'grommet';
import { BlendBridge } from 'blb-types/blendBridge';
import { respondToInvite } from 'blb-api/groups';
import { getPersona } from 'blb-persona/index';
import { MeContext } from 'blb-contexts/Me';
import { Button } from '@blend/alchemy';

const ModalStyles = {
  content: {
    height: 'fit-content',
    width: '500px',
  },
};

interface TeamsInvitationModalProps {
  invitations: BlendBridge.API.GroupInvitationResponse[];
  isOpen: boolean;
  handleClose: (shouldRefetch?: boolean) => void;
}

const TeamsInvitationModal: React.FC<TeamsInvitationModalProps> = ({ invitations, isOpen, handleClose }) => {
  const [currentInvitationIndex, setCurrentInvitationIndex] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<Error>();
  const me = React.useContext(MeContext)!;
  const persona = getPersona(me.principalType);
  const supportInfo = persona.getSupportContactInfo();
  const Toaster = useToasts()!;

  const totalInvitationsDescription =
    invitations.length > 1 ? ` ${currentInvitationIndex + 1}/${invitations.length}` : '';

  const respondToInvitation = async (assignmentId: string, response: BlendBridge.API.GroupInvitationStatuses) => {
    try {
      setIsSubmitting(true);
      await respondToInvite(assignmentId, { response });
      Toaster.addToast({
        message: `Successfully ${response.toLowerCase()} invite for ${invitations[currentInvitationIndex].groupName}`,
        theme: 'info',
      });

      if (invitations[currentInvitationIndex + 1]) {
        setCurrentInvitationIndex(currentInvitationIndex + 1);
      } else {
        handleClose();
      }
      setIsSubmitting(false);
    } catch (e) {
      const err = e as Error;
      setError(err);
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      buttonComponents={
        <>
          <Button
            disabled={isSubmitting}
            id={`it-decline-invite-btn`}
            label="Decline"
            onClick={() =>
              respondToInvitation(
                invitations[currentInvitationIndex].groupAssignmentId,
                BlendBridge.API.GroupInvitationStatuses.DECLINED,
              )
            }
            type="submit"
            variant="secondary"
          />
          <Button
            disabled={isSubmitting}
            id={`it-accept-invite-btn`}
            label="Accept"
            onClick={() =>
              respondToInvitation(
                invitations[currentInvitationIndex].groupAssignmentId,
                BlendBridge.API.GroupInvitationStatuses.ACCEPTED,
              )
            }
            primary
            type="submit"
          />
        </>
      }
      handleClose={handleClose}
      id="outstanding-invitations-modal"
      isOpen={isOpen}
      style={ModalStyles}
      title={`Accept invite${totalInvitationsDescription}`}
    >
      {error && (
        <AlertBar
          boxProps={{ margin: { bottom: 'medium' } }}
          errorMessage={`Error while trying respond to invite. Please try again or contact support at ${supportInfo.email}`}
          id="it-invite-response-error"
        />
      )}

      <dl>
        <dt>
          <Text size="small" weight="bolder">
            Team
          </Text>
        </dt>
        <dd>
          <Text margin={{ top: '4px' }} size="medium">
            {invitations[currentInvitationIndex].groupName}
          </Text>
        </dd>
        <dt>
          <Text size="small" weight="bolder">
            Inviter Email
          </Text>
        </dt>
        <dd>
          <Text margin={{ top: '4px' }} size="medium">
            {invitations[currentInvitationIndex].inviterUsername}
          </Text>
        </dd>
      </dl>

      <Text as="p" margin={{ top: '12px' }} size="medium">
        By accepting this invitation, you agree to have your closings shared and accessed by the rest of your team.
      </Text>
    </Modal>
  );
};

export default TeamsInvitationModal;
